import { initialDoc } from '@databases/collab-migrations/migrations/initial-doc';
import { initialView } from '@databases/collab-migrations/migrations/initial-view';
import { type CollabManager } from '@databases/collab-state-manager/collab-manager';
import { applyTemplateExperience } from '@databases/frontend-metrics';
import { logError } from '@databases/logs';
import { type DatabaseJSON } from '@databases/model/database';
import { type CollabLayoutType } from '@databases/model/views/view';
import { type IntlShape } from 'react-intl-next';

import { processTemplate } from './process';
import { type TemplateType } from './types';

export const fetchTemplateByName = async (
  templateType: TemplateType
): Promise<DatabaseJSON | undefined> => {
  try {
    return await import(`../static/template_${templateType}.json`);
    // eslint-disable-next-line canvas/no-swallow-errors-in-catch
  } catch (error) {
    logError(new Error(`[Templates]: Failed to fetch template: ${templateType}`));
  }
};

const resetToDefault = (collabManager: CollabManager | undefined, intl: IntlShape) => {
  if (collabManager) {
    const documentId = collabManager.databaseAri.toString();
    const ydb = collabManager.database;

    ydb.reset();
    initialDoc.apply(documentId, ydb, intl);
    initialView.apply(documentId, ydb, intl);

    applyTemplateExperience.success();
  } else {
    applyTemplateExperience.failure();
  }
};

export const applyTemplate = async ({
  templateType,
  templateLayout,
  collabManager,
  intl
}: {
  templateType: TemplateType;
  templateLayout?: CollabLayoutType;
  collabManager: CollabManager | undefined;
  intl: IntlShape;
}): Promise<void> => {
  applyTemplateExperience.start();

  if (templateType === 'emptyDefault') {
    resetToDefault(collabManager, intl);
    return;
  }

  const databaseJson = await fetchTemplateByName(templateType);

  if (collabManager && databaseJson) {
    if (collabManager.isReadOnly) {
      return;
    }

    const template = processTemplate({
      templateType,
      databaseJson,
      intl,
      layoutType: templateLayout
    });

    const ydb = collabManager.database;

    ydb.mutate((database) => {
      database.replace(template);
    });

    applyTemplateExperience.success();
  } else {
    applyTemplateExperience.failure();
  }
};
